import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { ErrorModal, useUserContext } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	AgGridEvent,
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { isNil } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Section } from "../models/Section";
import {
	getSections,
	getSectionsByPart,
	SectionsResponse,
} from "../repositories/SectionRepository";

interface Props {
	sectionsOfPart: Section[];
	onSelectionChanged: Function;
}

type useSectionsByPartProps = {
	partId: number;
	onError?: (error: Error) => void;
};

type useSectionsProps = {
	sectionId?: number;
	refetchInterval?: number;
	assetId?: number;
	shiftId?: number;
	onError?: (error: Error) => void;
};

export const useSectionsByPart = ({
	partId,
	onError,
}: useSectionsByPartProps) => {
	return useQuery<Section[], Error>({
		queryKey: ["sectionsByPart", partId],
		queryFn: ({ signal }) => getSectionsByPart({ partId }, signal),
		onError: onError,
	});
};

export const useSections = ({
	sectionId,
	refetchInterval,
	assetId,
	shiftId,
	onError,
}: useSectionsProps) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	return useQuery<SectionsResponse, Error>({
		queryKey: ["sections", sectionId, assetId, defaultAreaId, shiftId],
		meta: { realtime: ["M2.GetSections"] },
		queryFn: ({ signal }) =>
			getSections(
				{ sectionId, defaultAreaId: assetId || defaultAreaId, shiftId },
				signal,
			),
		refetchInterval: refetchInterval,
		onError: onError,
	});
};

export const getCurrentSection = (data: SectionsResponse | undefined) => {
	const currentEvent = data?.upcomingEvents?.find(
		(event) => event.EventIndex === 1,
	);
	return currentEvent
		? (data?.sections.find(
				(section) => section.SectionId === currentEvent.SectionId,
			) ?? null)
		: null;
};

export const SectionsOfPartSelector = ({
	sectionsOfPart,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [allSections, setAllSections] = useState<Section[]>([]);
	const [error, setError] = useState<string>("");

	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { data, isLoading, isSuccess } = useSections({
		onError: (error) => setError(error.message),
	});

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	const sortRows = (event: AgGridEvent) => {
		event.api.applyColumnState({
			state: [{ colId: "SectionName", sort: "asc", sortIndex: 0 }],
		});
	};

	const rows = useMemo(() => {
		return allSections.map(
			({ SectionId, SectionName, SectionFamilyName, SectionFamilyId }) => {
				return {
					SectionId: SectionId,
					SectionName: SectionName,
					SectionFamilyName: SectionFamilyName,
					SectionFamilyId: SectionFamilyId,
				};
			},
		);
	}, [allSections]);

	const columns: ColDef[] = [
		{
			field: "",
			flex: 1,
			maxWidth: 100,
			checkboxSelection: true,
			showDisabledCheckboxes: true,
			sortIndex: 1,
			sortable: false,
		},
		{
			field: "SectionName",
			headerName: "Section",
			flex: 2,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.SectionName?.localeCompare(nodeB.data.SectionName);
				}
			},
		},
		{
			field: "SectionFamilyName",
			headerName: "Family",
			flex: 2,
		},
	];

	const { defaultColDef } = useGridDefinitions({});

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows(event);
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			params.api.forEachNode((node) => {
				node.setSelected(
					!!node.data &&
						!isNil(
							sectionsOfPart.find((p) => p.SectionId === node.data.SectionId),
						),
				);
			});
			sortRows(params);
		},
		[sortRows],
	);

	const onGridReady = useCallback(
		(event: GridReadyEvent) => {
			setGridApi(event.api);
		},
		[setGridApi],
	);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	useEffect(() => {
		if (data?.sections && data.sections.length > 0) {
			setAllSections(data.sections);
		}
	}, [isSuccess, data]);

	return (
		<>
			<Grid2
				container
				size={{ xs: 12, md: 12 }}
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						id="outlined-basic"
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						style={{ width: "100%" }}
						slotProps={{
							htmlInput: { "data-test-id": "search-sections-field" },
						}}
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "40vh" }}>
				<AgGridReact
					loading={isLoading}
					getRowId={(params) => params.data.SectionName}
					onGridReady={onGridReady}
					rowHeight={25}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					animateRows
					onSelectionChanged={onAgGridSelectionChanged}
					gridOptions={{
						rowSelection: {
							mode: "multiRow",
							checkboxes: false,
							enableSelectionWithoutKeys: true,
							enableClickSelection: true,
						},
					}}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
